import { useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useClientsQuery } from '@/features/Clients/Data';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import { textoCorresponde } from '@/features/SI/utils/buscaTexto';

interface SeletorProps {
  selecionados: string[];
  onChange: (ids: string[]) => void;
}

/** Chips dos escolhidos + busca com lista de sugestões (até 8). */
function SeletorBusca({
  selecionados,
  onChange,
  opcoes,
  placeholder,
}: SeletorProps & { opcoes: Array<{ id: string; nome: string }>; placeholder: string }) {
  const [busca, setBusca] = useState('');
  const porId = useMemo(() => new Map(opcoes.map((o) => [o.id, o.nome])), [opcoes]);
  const sugestoes = busca.trim()
    ? opcoes.filter((o) => !selecionados.includes(o.id) && textoCorresponde([o.nome], busca)).slice(0, 8)
    : [];

  return (
    <div className="mt-2 rounded-lg border border-raro-gray-300 p-2">
      {selecionados.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {selecionados.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-full bg-raro-gray-100 px-2 py-0.5 text-xs text-raro-gray-700">
              {porId.get(id) ?? id}
              <button type="button" aria-label="Remover" onClick={() => onChange(selecionados.filter((s) => s !== id))}>
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        className="w-full rounded-md border border-raro-gray-200 px-2 py-1 text-sm"
        placeholder={placeholder}
        value={busca}
        onChange={(event) => setBusca(event.target.value)}
      />
      {sugestoes.length > 0 && (
        <ul className="mt-1 max-h-48 overflow-y-auto rounded-md border border-raro-gray-100 text-sm">
          {sugestoes.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className="w-full px-2 py-1.5 text-left hover:bg-primary-50"
                onClick={() => {
                  onChange([...selecionados, o.id]);
                  setBusca('');
                }}
              >
                {o.nome}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SeletorProjetos(props: SeletorProps) {
  const { data: clientes = [] } = useClientsQuery();
  const opcoes = useMemo(() => clientes.map((c) => ({ id: c.id, nome: c.name })), [clientes]);
  return <SeletorBusca {...props} opcoes={opcoes} placeholder="Buscar projeto (cliente)..." />;
}

export function SeletorPessoas(props: SeletorProps) {
  const { data: colaboradores = [] } = useCollaboratorsPublicQuery();
  const opcoes = useMemo(() => colaboradores.map((c) => ({ id: c.id, nome: c.name })), [colaboradores]);
  return <SeletorBusca {...props} opcoes={opcoes} placeholder="Buscar colaborador..." />;
}
